/**
 * Bookings.tsx — Admin Venue Booking Management
 * Full inline-style implementation — no Tailwind dependency.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import { useToast } from '../../context/ToastContext';

// ── Types ─────────────────────────────────────────────────────────────────────
interface BookingSpace {
  id: string;
  name: string;
  description?: string;
  category?: string;
  location?: string;
  capacity?: number | null;
  protegeAccessLevelId?: number | null;
  protegeDoorId?: number | null;
  hourlyRate?: number | null;
  dailyRate?: number | null;
  minDurationHours?: number | null;
  maxDurationHours?: number | null;
  bookingLeadDays?: number | null;
  amenities?: string[];
  isActive?: boolean;
  autoConfirm?: boolean;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: string;
  imageUrl?: string;
}

interface Booking {
  id: string;
  reference: string;
  spaceId?: string;
  spaceName?: string;
  space_name?: string;
  bookingDate?: string;
  booking_date?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  customer_name?: string;
  email?: string;
  customer_email?: string;
  phone?: string;
  customer_phone?: string;
  purpose?: string;
  notes?: string;
  status: string;
  pinCode?: string;
  pin_code?: string;
  protegeSynced?: boolean;
  protege_synced?: boolean;
  protegeSyncError?: string | null;
  totalCost?: number | null;
  total_cost?: number | null;
  paymentStatus?: string;
  payment_status?: string;
  createdAt?: string;
  created_at?: string;
  auditLog?: AuditEntry[];
  audit_log?: AuditEntry[];
}

interface AuditEntry {
  id: string;
  action: string;
  details?: string;
  performedBy?: string;
  performed_by?: string;
  createdAt?: string;
  created_at?: string;
}

interface RecurringTemplate {
  id: string;
  spaceId?: string;
  space_id?: string;
  spaceName?: string;
  space_name?: string;
  pattern: string;
  startDate?: string;
  start_date?: string;
  endDate?: string;
  end_date?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  status: string;
}

interface DashboardStats {
  todayCount: number;
  activeCount: number;
  upcomingCount: number;
  pendingCount: number;
  todayRevenue?: number;
  weekRevenue?: number;
  spaceUtilisation?: { spaceId: string; spaceName: string; rate: number }[];
}

// ── API ───────────────────────────────────────────────────────────────────────
const API = '/api/bookings';

function tok() {
  return localStorage.getItem('lemai_token') || localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
}
function hdrs(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` };
}
async function apiFetch(url: string, opts: RequestInit = {}) {
  const res = await fetch(url, { headers: hdrs(), ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d?: string) {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}
function fmtTime(t?: string) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h > 12 ? h - 12 : h || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}
function bName(b: Booking) {
  return b.customer_name || `${b.firstName || b.first_name || ''} ${b.lastName || b.last_name || ''}`.trim() || '—';
}
function bEmail(b: Booking) { return b.customer_email || b.email || '—'; }
function bPhone(b: Booking) { return b.customer_phone || b.phone || '—'; }
function bDate(b: Booking) { return b.bookingDate || b.booking_date || ''; }
function bStart(b: Booking) { return b.startTime || b.start_time || ''; }
function bEnd(b: Booking) { return b.endTime || b.end_time || ''; }
function bSpace(b: Booking) { return b.spaceName || b.space_name || '—'; }
function bPin(b: Booking) { return b.pinCode || b.pin_code; }
function bSynced(b: Booking) { return b.protegeSynced ?? b.protege_synced; }
function bCost(b: Booking) { return b.totalCost ?? b.total_cost; }
function bAudit(b: Booking): AuditEntry[] { return b.auditLog || b.audit_log || []; }
function todayStr() { return new Date().toISOString().slice(0,10); }

const STATUS_META: Record<string, { bg: string; text: string; border: string; label: string }> = {
  pending:   { bg: '#2d1f00', text: '#f59e0b', border: '#92400e', label: 'Pending' },
  confirmed: { bg: '#0c1e3d', text: '#60a5fa', border: '#1e3a5f', label: 'Confirmed' },
  approved:  { bg: '#0c1e3d', text: '#60a5fa', border: '#1e3a5f', label: 'Approved' },
  active:    { bg: '#052e16', text: '#16a34a', border: '#14532d', label: 'Active' },
  completed: { bg: '#1a1a1a', text: '#888888', border: '#333333', label: 'Completed' },
  cancelled: { bg: '#450a0a', text: '#dc2626', border: '#7f1d1d', label: 'Cancelled' },
  rejected:  { bg: '#450a0a', text: '#dc2626', border: '#7f1d1d', label: 'Rejected' },
};
function StatusPill({ status }: { status: string }) {
  const m = STATUS_META[status] || { bg: '#1a1a1a', text: '#888888', border: '#333333', label: status };
  return (
    <span style={{ background: m.bg, color: m.text, border: `1px solid ${m.border}`, borderRadius: 2, padding: '1px 6px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

// ── Shared UI atoms ───────────────────────────────────────────────────────────
function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
const INPUT_STYLE: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1px solid #333', borderRadius: 2, fontSize: 12, outline: 'none', background: '#1a1a1a', color: '#e5e5e5', boxSizing: 'border-box', fontFamily: 'inherit' };
const SELECT_STYLE: React.CSSProperties = { ...INPUT_STYLE, cursor: 'pointer' };

function Btn({ onClick, disabled, variant = 'primary', size = 'md', children }: {
  onClick?: () => void; disabled?: boolean; variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success'; size?: 'sm' | 'md'; children: React.ReactNode;
}) {
  const colors = {
    primary:   { bg: '#1e3a5f', hover: '#1e40af', text: '#60a5fa', border: '#1e3a5f' },
    secondary: { bg: '#1a1a1a', hover: '#222',    text: '#888888', border: '#333333' },
    danger:    { bg: '#450a0a', hover: '#7f1d1d', text: '#dc2626', border: '#7f1d1d' },
    ghost:     { bg: 'transparent', hover: '#1a1a1a', text: '#666', border: 'transparent' },
    success:   { bg: '#052e16', hover: '#14532d', text: '#16a34a', border: '#14532d' },
  };
  const c = colors[variant];
  const pad = size === 'sm' ? '3px 10px' : '5px 14px';
  const fz = size === 'sm' ? 10 : 11;
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ padding: pad, background: disabled ? '#1a1a1a' : hov ? c.hover : c.bg, color: disabled ? '#444' : c.text, border: `1px solid ${disabled ? '#333' : c.border}`, borderRadius: 2, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: fz, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'all 0.15s', fontFamily: 'inherit', whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>
      {children}
    </button>
  );
}

function Alert({ type, children }: { type: 'error' | 'warning' | 'info' | 'success'; children: React.ReactNode }) {
  const c = { error: ['#450a0a','#7f1d1d','#dc2626','⚠'], warning: ['#2d1f00','#92400e','#f59e0b','⚡'], info: ['#0c1e3d','#1e3a5f','#60a5fa','ℹ'], success: ['#052e16','#14532d','#16a34a','✓'] }[type];
  return (
    <div style={{ background: c[0], border: `1px solid ${c[1]}`, borderRadius: 2, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11 }}>
      <span style={{ color: c[2], fontWeight: 700 }}>{c[3]}</span>
      <span style={{ color: c[2] }}>{children}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <div style={{ width: 24, height: 24, border: '2px solid #222', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Space Form Modal ──────────────────────────────────────────────────────────
const CATEGORIES = ['Meeting Room', 'Community Hall', 'Sports', 'Outdoor', 'Pavilion', 'Event Space', 'Recreation', 'Conference', 'Other'];

interface ProtegeAL { id: string; name: string; }
interface ProtegeDoor { id: string; name: string; controllerId?: string; }

function SpaceModal({ space, onClose, onSave }: { space: Partial<BookingSpace> | null; onClose: () => void; onSave: () => void }) {
  const [form, setForm] = useState<Partial<BookingSpace>>(space && Object.keys(space).length ? { ...space } : {
    name: '', description: '', category: '', location: '', capacity: null,
    protegeAccessLevelId: null, protegeDoorId: null,
    hourlyRate: null, dailyRate: null,
    minDurationHours: 1, maxDurationHours: 8, bookingLeadDays: 1,
    amenities: [], isActive: true, autoConfirm: false,
    availableFrom: '08:00', availableTo: '22:00', availableDays: '1,2,3,4,5,6,7',
    imageUrl: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [amenityInput, setAmenityInput] = useState('');
  const [protegeSection, setProtegeSection] = useState(true);
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgPreview, setImgPreview] = useState<string>(space?.imageUrl || '');
  const [imgUploading, setImgUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Live Protege GX data — only Bookable access levels (record group 129)
  const [accessLevels, setAccessLevels] = useState<ProtegeAL[]>([]);
  const [protegeLoading, setProtegeLoading] = useState(false);
  const [protegeErr, setProtegeErr] = useState('');
  const [alSearch, setAlSearch] = useState('');

  useEffect(() => {
    if (!protegeSection) return;
    setProtegeLoading(true);
    setProtegeErr('');
    apiFetch(`${API}/protege-access-levels`)
      .then(d => { setAccessLevels(d.accessLevels || []); if (d.error) setProtegeErr(d.error); })
      .catch(e => setProtegeErr(e.message))
      .finally(() => setProtegeLoading(false));
  }, [protegeSection]);

  const set = (k: keyof BookingSpace) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const v = e.target.type === 'number' ? (e.target.value ? Number(e.target.value) : null)
            : e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked
            : e.target.value;
    setForm(p => ({ ...p, [k]: v }));
  };

  const addAmenity = () => {
    const a = amenityInput.trim();
    if (a && !form.amenities?.includes(a)) setForm(f => ({ ...f, amenities: [...(f.amenities || []), a] }));
    setAmenityInput('');
  };

  const handleImgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgFile(f);
    const reader = new FileReader();
    reader.onload = ev => setImgPreview(ev.target?.result as string);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.name?.trim()) { setErr('Space name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, amenities: form.amenities || [] };
      let saved: any;
      if (form.id) {
        saved = await apiFetch(`${API}/spaces/${form.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        saved = await apiFetch(`${API}/spaces`, { method: 'POST', body: JSON.stringify(payload) });
      }
      // Upload image if one was selected
      if (imgFile && saved?.id) {
        setImgUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', imgFile);
          await fetch(`${API}/spaces/${saved.id}/image`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${tok()}` },
            body: fd,
          });
        } catch { /* image upload failure is non-fatal */ }
        finally { setImgUploading(false); }
      }
      onSave();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '32px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: '#1d4ed8', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>{form.id ? 'Edit Venue Space' : 'Add Venue Space'}</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>Configure the space and Protege access control mapping</div>
          </div>
          <button onClick={onClose} style={{ color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {err && <Alert type="error">{err}</Alert>}

          {/* Basic info */}
          <SectionLabel>Basic Information</SectionLabel>
          <Field label="Space Name *">
            <input style={INPUT_STYLE} value={form.name || ''} onChange={set('name')} placeholder="e.g. Council Chamber" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Category">
              <select style={SELECT_STYLE} value={form.category || ''} onChange={set('category')}>
                <option value="">Select category…</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Location / Room">
              <input style={INPUT_STYLE} value={form.location || ''} onChange={set('location')} placeholder="e.g. Level 2, Civic Centre" />
            </Field>
          </div>
          <Field label="Description">
            <textarea style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 72 }} value={form.description || ''} onChange={set('description') as any} placeholder="Brief description of the space and its uses…" />
          </Field>
          <Field label="Capacity (people)">
            <input type="number" min="0" style={{ ...INPUT_STYLE, maxWidth: 140 }} value={form.capacity ?? ''} onChange={set('capacity')} placeholder="0" />
          </Field>

          {/* Protege mapping */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <button onClick={() => setProtegeSection(p => !p)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'left' }}>
              <SectionLabel style={{ margin: 0 }}>🔐 Protege GX Access Control Mapping</SectionLabel>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>{protegeSection ? '▾ hide' : '▸ show'}</span>
            </button>
            {protegeSection && (
              <div style={{ marginTop: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                  When a booking is confirmed, Lemai creates a temporary Protege GX cardholder in the selected access level group. The booker receives a 6-digit PIN for keypad entry. The cardholder is removed when the booking is cancelled or checked out.
                </div>
                {protegeLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b7280', padding: '8px 0' }}>
                    <div style={{ width: 14, height: 14, border: '2px solid #bfdbfe', borderTop: '2px solid #1d4ed8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Loading Bookable rooms from Protege GX…
                  </div>
                ) : (
                  <>
                    {protegeErr && (
                      <Alert type="warning">Could not load rooms from Protege GX: {protegeErr}. Configure credentials in the Settings tab first.</Alert>
                    )}
                    <Field label="Booking Room" hint={accessLevels.length ? `${accessLevels.length} rooms available — type to filter` : 'Could not load rooms — check Settings → Protege Integration'}>
                      {accessLevels.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <input
                            style={{ ...INPUT_STYLE, background: '#f8fafc' }}
                            placeholder="Search rooms…"
                            value={alSearch}
                            onChange={e => setAlSearch(e.target.value)}
                          />
                          <select style={SELECT_STYLE}
                            value={String(form.protegeAccessLevelId ?? '')}
                            onChange={e => setForm(p => ({ ...p, protegeAccessLevelId: e.target.value ? Number(e.target.value) : null }))}>
                            <option value="">— Select a bookable room —</option>
                            {accessLevels
                              .filter(al => !alSearch || al.name.toLowerCase().includes(alSearch.toLowerCase()))
                              .map(al => (
                                <option key={al.id} value={al.id}>{al.name}</option>
                              ))}
                          </select>
                        </div>
                      ) : (
                        <input type="number" style={INPUT_STYLE} value={form.protegeAccessLevelId ?? ''}
                          onChange={set('protegeAccessLevelId')} placeholder="Enter access level ID manually" />
                      )}
                    </Field>
                    {form.protegeAccessLevelId ? (
                      <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#166534' }}>
                        ✓ Confirmed bookings will create a Protege GX cardholder in the selected room's access group
                      </div>
                    ) : (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e' }}>
                        ⚠ No room selected — Protege PIN provisioning will be skipped for this space
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Pricing */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <SectionLabel>Pricing & Availability</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 14 }}>
              <Field label="Hourly Rate ($)" hint="Leave blank for free">
                <input type="number" min="0" step="0.50" style={INPUT_STYLE} value={form.hourlyRate ?? ''} onChange={set('hourlyRate')} placeholder="0.00" />
              </Field>
              <Field label="Daily Rate ($)" hint="Optional daily cap">
                <input type="number" min="0" step="5" style={INPUT_STYLE} value={form.dailyRate ?? ''} onChange={set('dailyRate')} placeholder="0.00" />
              </Field>
              <Field label="Advance Booking (days)" hint="Max days ahead to book">
                <input type="number" min="1" style={INPUT_STYLE} value={form.bookingLeadDays ?? ''} onChange={set('bookingLeadDays')} placeholder="30" />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <Field label="Min Duration (hours)">
                <input type="number" min="0.5" step="0.5" style={INPUT_STYLE} value={form.minDurationHours ?? ''} onChange={set('minDurationHours')} placeholder="1" />
              </Field>
              <Field label="Max Duration (hours)">
                <input type="number" min="1" style={INPUT_STYLE} value={form.maxDurationHours ?? ''} onChange={set('maxDurationHours')} placeholder="8" />
              </Field>
            </div>
          </div>


          {/* Venue Photo */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <SectionLabel>Venue Photo</SectionLabel>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              {imgPreview ? (
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img src={imgPreview} alt="Venue" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <button onClick={() => { setImgPreview(''); setImgFile(null); setForm(p => ({ ...p, imageUrl: '' })); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#dc2626', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
              ) : (
                <div style={{ width: 120, height: 80, borderRadius: 8, border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#9ca3af', fontSize: 28 }}>🖼</div>
              )}
              <div style={{ flex: 1 }}>
                <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleImgChange} style={{ display: 'none' }} />
                <Btn onClick={() => fileInputRef.current?.click()} variant="secondary" size="sm">📷 Choose Photo</Btn>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>JPG, PNG, GIF, WebP · Shown on public booking portal</div>
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <SectionLabel>Amenities</SectionLabel>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input style={{ ...INPUT_STYLE, flex: 1 }} value={amenityInput} onChange={e => setAmenityInput(e.target.value)}
                placeholder="e.g. Projector, Whiteboard, Kitchen" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAmenity(); } }} />
              <Btn onClick={addAmenity} variant="secondary">Add</Btn>
            </div>
            {(form.amenities || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {(form.amenities || []).map(a => (
                  <span key={a} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 20, padding: '3px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {a}
                    <button onClick={() => setForm(p => ({ ...p, amenities: p.amenities?.filter(x => x !== a) }))}
                      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Booking Policy */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <SectionLabel>Booking Policy</SectionLabel>

            {/* Auto-confirm toggle */}
            <div style={{ marginTop: 14, background: form.autoConfirm ? '#f0fdf4' : '#f8fafc', border: `1px solid ${form.autoConfirm ? '#86efac' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.autoConfirm ?? false} onChange={e => setForm(p => ({ ...p, autoConfirm: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: form.autoConfirm ? '#166534' : '#374151' }}>
                    {form.autoConfirm ? '✓ Auto-confirm bookings' : 'Require admin approval'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {form.autoConfirm
                      ? 'Bookings are instantly confirmed and a Protege PIN is issued automatically'
                      : 'Each booking stays pending until manually confirmed by an admin'}
                  </div>
                </div>
              </label>
            </div>

            {/* Available hours */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <Field label="Available From" hint="Earliest booking start time">
                <input type="time" style={INPUT_STYLE} value={form.availableFrom || '08:00'} onChange={e => setForm(p => ({ ...p, availableFrom: e.target.value }))} />
              </Field>
              <Field label="Available Until" hint="Latest booking end time">
                <input type="time" style={INPUT_STYLE} value={form.availableTo || '22:00'} onChange={e => setForm(p => ({ ...p, availableTo: e.target.value }))} />
              </Field>
            </div>

            {/* Available days */}
            <Field label="Available Days">
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {[['1','Mon'],['2','Tue'],['3','Wed'],['4','Thu'],['5','Fri'],['6','Sat'],['7','Sun']].map(([v, label]) => {
                  const days = (form.availableDays || '1,2,3,4,5,6,7').split(',').filter(Boolean);
                  const active = days.includes(v);
                  const toggle = () => {
                    const next = active ? days.filter(d => d !== v) : [...days, v];
                    next.sort();
                    setForm(p => ({ ...p, availableDays: next.join(',') }));
                  };
                  return (
                    <button key={v} onClick={toggle} type="button"
                      style={{ flex: 1, padding: '6px 2px', borderRadius: 6, border: `1.5px solid ${active ? '#1d4ed8' : '#d1d5db'}`, background: active ? '#1d4ed8' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          {/* Active toggle */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive ?? true} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#1d4ed8', cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Space is active</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Active spaces are visible on the public booking portal</div>
              </div>
            </label>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
          <Btn onClick={onClose} variant="secondary">Cancel</Btn>
          <Btn onClick={save} disabled={saving || imgUploading}>{imgUploading ? 'Uploading photo…' : saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create Space'}</Btn>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: 9, fontWeight: 700, color: '#555', letterSpacing: '0.12em', textTransform: 'uppercase', ...style }}>{children}</div>;
}

// ── Admin New Booking Modal ────────────────────────────────────────────────────
function NewBookingModal({ spaces, onClose, onSave }: { spaces: BookingSpace[]; onClose: () => void; onSave: () => void }) {
  const [form, setForm] = useState({
    spaceId: spaces[0]?.id || '',
    bookingDate: todayStr(),
    startTime: '09:00',
    endTime: '10:00',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    purpose: '',
    notes: '',
    attendees: '1',
    autoConfirm: true,
    paymentStatus: 'unpaid',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [slots, setSlots] = useState<{start: string; end: string; available: boolean}[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState<{reference: string; pinCode?: string; status: string; spaceName: string; bookingDate: string; startTime: string; endTime: string} | null>(null);
  const { toast } = useToast();

  const f = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  // Load availability slots when space or date changes
  useEffect(() => {
    if (!form.spaceId || !form.bookingDate) return;
    setSlotsLoading(true);
    fetch('/api/public/bookings/availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ space_id: form.spaceId, date: form.bookingDate }),
    }).then(r => r.json()).then(d => setSlots(d.slots || [])).catch(() => setSlots([])).finally(() => setSlotsLoading(false));
  }, [form.spaceId, form.bookingDate]);

  const selectedSpace = spaces.find(s => s.id === form.spaceId);

  const save = async () => {
    if (!form.spaceId || !form.firstName.trim() || !form.email.trim()) {
      setErr('Space, first name, and email are required'); return;
    }
    if (!form.bookingDate || !form.startTime || !form.endTime) {
      setErr('Date and time are required'); return;
    }
    setSaving(true); setErr('');
    try {
      const result = await apiFetch(`${API}/`, {
        method: 'POST',
        body: JSON.stringify({
          spaceId: form.spaceId,
          bookingDate: form.bookingDate,
          startTime: form.startTime,
          endTime: form.endTime,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          purpose: form.purpose.trim() || undefined,
          notes: form.notes.trim() || undefined,
          attendees: parseInt(form.attendees) || 1,
          autoConfirm: form.autoConfirm,
          paymentStatus: form.paymentStatus,
        }),
      });
      toast(`Booking ${result.reference} created${result.status === 'confirmed' ? ' & confirmed' : ''}`, 'success');
      // Show PIN confirmation screen if booking was confirmed (has a PIN)
      if (result.pinCode || result.status === 'confirmed') {
        setCreatedResult({
          reference: result.reference,
          pinCode: result.pinCode,
          status: result.status,
          spaceName: result.spaceName || '',
          bookingDate: result.bookingDate || form.bookingDate,
          startTime: result.startTime || form.startTime,
          endTime: result.endTime || form.endTime,
        });
        onSave(); // refresh the list in background
      } else {
        onSave();
      }
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // PIN confirmation screen — shown after a confirmed booking is created
  if (createdResult) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '32px 16px' }}>
        <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <div style={{ background: '#16a34a', padding: '20px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>Booking Confirmed!</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 4, fontFamily: 'monospace' }}>{createdResult.reference}</div>
          </div>
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#374151' }}>
              <div><strong>Venue:</strong> {createdResult.spaceName}</div>
              <div><strong>Date:</strong> {fmtDate(createdResult.bookingDate)}</div>
              <div><strong>Time:</strong> {fmtTime(createdResult.startTime)} – {fmtTime(createdResult.endTime)}</div>
            </div>
            {createdResult.pinCode ? (
              <div style={{ background: '#0d0d0d', borderRadius: 12, padding: '20px', textAlign: 'center' }}>
                <div style={{ color: '#a0a0a0', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Access PIN — share with customer</div>
                <div style={{ fontFamily: 'monospace', fontSize: 42, fontWeight: 900, letterSpacing: '0.25em', color: '#4ade80' }}>{createdResult.pinCode}</div>
                <div style={{ color: '#555555', fontSize: 11, marginTop: 10 }}>Customer enters this at the door keypad during their booking window</div>
              </div>
            ) : (
              <Alert type="info">Booking confirmed. PIN will be available in the booking detail once Protege GX syncs.</Alert>
            )}
            <Alert type="warning">No email configured — PIN not emailed. Copy it now or find it later in Booking Details.</Alert>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Btn onClick={onClose} variant="primary">Done</Btn>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '32px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: '#0d0d0d', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>New Booking</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>Create a booking as an administrator</div>
          </div>
          <button onClick={onClose} style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {err && <Alert type="error">{err}</Alert>}

          {/* Venue & Date */}
          <SectionLabel>Booking Details</SectionLabel>
          <Field label="Venue Space *">
            <select style={SELECT_STYLE} value={form.spaceId} onChange={f('spaceId')}>
              <option value="">— Select a space —</option>
              {spaces.filter(s => s.isActive !== false).map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.location ? ` · ${s.location}` : ''}{s.protegeAccessLevelId ? ' 🔐' : ' ⚠'}
                </option>
              ))}
            </select>
            {selectedSpace && !selectedSpace.protegeAccessLevelId && (
              <div style={{ fontSize: 11, color: '#d97706', marginTop: 4 }}>⚠ No booking room assigned to this space — PIN provisioning will be skipped</div>
            )}
            {selectedSpace && (
              <div style={{ fontSize: 11, marginTop: 4, color: selectedSpace.autoConfirm ? '#16a34a' : '#d97706' }}>
                {selectedSpace.autoConfirm ? '⚡ Space is set to auto-confirm — booking will be immediately confirmed' : '👤 Space requires admin approval'}
              </div>
            )}
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Date *">
              <input type="date" style={INPUT_STYLE} value={form.bookingDate} onChange={f('bookingDate')} min={todayStr()} />
            </Field>
            <Field label="Start Time *">
              <input type="time" style={INPUT_STYLE} value={form.startTime} onChange={f('startTime')} />
            </Field>
            <Field label="End Time *">
              <input type="time" style={INPUT_STYLE} value={form.endTime} onChange={f('endTime')} />
            </Field>
          </div>

          {/* Availability grid */}
          {form.spaceId && form.bookingDate && (
            <div style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {slotsLoading ? 'Loading availability…' : `Availability on ${form.bookingDate}`}
              </div>
              {!slotsLoading && slots.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {slots.map(s => (
                    <button key={s.start} onClick={() => {
                      if (!s.available) return;
                      setForm(p => ({ ...p, startTime: s.start, endTime: s.end }));
                    }}
                      style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: s.available ? 'pointer' : 'not-allowed',
                        background: !s.available ? '#fee2e2' : (form.startTime === s.start ? '#1d4ed8' : '#f1f5f9'),
                        color: !s.available ? '#dc2626' : (form.startTime === s.start ? '#fff' : '#374151'),
                        border: `1px solid ${!s.available ? '#fca5a5' : form.startTime === s.start ? '#1d4ed8' : '#e5e7eb'}`,
                      }}>
                      {fmtTime(s.start)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Customer */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <SectionLabel style={{ marginBottom: 12 }}>Customer Information</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="First Name *">
                <input style={INPUT_STYLE} value={form.firstName} onChange={f('firstName')} placeholder="Jane" />
              </Field>
              <Field label="Last Name">
                <input style={INPUT_STYLE} value={form.lastName} onChange={f('lastName')} placeholder="Smith" />
              </Field>
              <Field label="Email *">
                <input type="email" style={INPUT_STYLE} value={form.email} onChange={f('email')} placeholder="jane@example.com" />
              </Field>
              <Field label="Phone">
                <input type="tel" style={INPUT_STYLE} value={form.phone} onChange={f('phone')} placeholder="0400 000 000" />
              </Field>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Attendees">
                <input type="number" min="1" style={INPUT_STYLE} value={form.attendees} onChange={f('attendees')} />
              </Field>
              <Field label="Payment Status">
                <select style={SELECT_STYLE} value={form.paymentStatus} onChange={f('paymentStatus')}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="waived">Waived</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Purpose */}
          <Field label="Purpose / Notes">
            <textarea style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 60 }}
              value={form.purpose} onChange={f('purpose') as any}
              placeholder="e.g. Monthly committee meeting, corporate function…" />
          </Field>

          {/* Auto-confirm toggle */}
          <div style={{ background: form.autoConfirm ? '#f0fdf4' : '#fffbeb', border: `1px solid ${form.autoConfirm ? '#86efac' : '#fde68a'}`, borderRadius: 10, padding: '12px 16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.autoConfirm}
                onChange={e => setForm(p => ({ ...p, autoConfirm: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: form.autoConfirm ? '#166534' : '#92400e' }}>
                  {form.autoConfirm ? '✓ Auto-confirm & sync to Protege GX' : 'Leave as Pending — confirm later'}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  {form.autoConfirm
                    ? 'Booking will be immediately confirmed and a cardholder created in Protege GX with a PIN'
                    : 'Booking stays pending until manually confirmed via the Bookings tab'}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedSpace?.hourlyRate ? (
              (() => {
                const [sh, sm] = form.startTime.split(':').map(Number);
                const [eh, em] = form.endTime.split(':').map(Number);
                const hrs = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                const cost = hrs > 0 ? (hrs * (selectedSpace.hourlyRate || 0)).toFixed(2) : '0.00';
                return `Est. cost: $${cost} (${hrs > 0 ? hrs : 0}h × $${selectedSpace.hourlyRate}/hr)`;
              })()
            ) : 'Free venue'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={onClose} variant="secondary">Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create Booking'}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Booking Detail Modal ──────────────────────────────────────────────────────
function BookingDetailModal({ bookingId, onClose, onRefresh }: { bookingId: string; onClose: () => void; onRefresh: () => void }) {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState('');
  const [err, setErr] = useState('');
  const [pinVisible, setPinVisible] = useState(true); // default visible — no SMTP so admin must copy PIN

  const load = useCallback(async () => {
    setLoading(true);
    try { setBooking(await apiFetch(`${API}/${bookingId}`)); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action: string, payload?: object) => {
    setActing(action); setErr('');
    try {
      await apiFetch(`${API}/${bookingId}/${action}`, { method: 'POST', body: JSON.stringify(payload || {}) });
      await load(); onRefresh();
    } catch (e: any) { setErr(e.message); }
    finally { setActing(''); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '32px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: '#0d0d0d', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Booking Details</div>
            {booking && <div style={{ color: '#a0a0a0', fontSize: 12, marginTop: 2, fontFamily: 'monospace' }}>{booking.reference}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {booking && <StatusPill status={booking.status} />}
            <button onClick={onClose} style={{ color: '#a0a0a0', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
        </div>

        {loading ? <Spinner /> : booking ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {err && <Alert type="error">{err}</Alert>}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
              {booking.status === 'pending' && (
                <Btn onClick={() => doAction('confirm')} disabled={!!acting} variant="success">
                  {acting === 'confirm' ? '…' : '✓'} Confirm Booking
                </Btn>
              )}
              {(booking.status === 'confirmed' || booking.status === 'approved') && (
                <Btn onClick={() => doAction('check-in')} disabled={!!acting} variant="primary">
                  {acting === 'check-in' ? '…' : '→'} Check In
                </Btn>
              )}
              {booking.status === 'active' && (
                <Btn onClick={() => doAction('check-out')} disabled={!!acting} variant="secondary">
                  {acting === 'check-out' ? '…' : '←'} Check Out
                </Btn>
              )}
              {!['completed', 'cancelled', 'rejected'].includes(booking.status) && (
                <Btn variant="danger" disabled={!!acting} onClick={() => {
                  const reason = window.prompt('Cancellation reason (optional):') ?? '';
                  doAction('cancel', { reason });
                }}>
                  {acting === 'cancel' ? '…' : '✕'} Cancel
                </Btn>
              )}
              {booking.status === 'confirmed' && !bSynced(booking) && (
                <Btn variant="secondary" disabled={!!acting} onClick={() => doAction('retry-protege-sync')}>
                  {acting === 'retry-protege-sync' ? '…' : '🔄'} Retry Protege Sync
                </Btn>
              )}
            </div>

            {/* Booking + customer info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <SectionLabel style={{ marginBottom: 12 }}>Booking Info</SectionLabel>
                <InfoRow icon="🏢" label="Venue" value={bSpace(booking)} />
                <InfoRow icon="📅" label="Date" value={fmtDate(bDate(booking))} />
                <InfoRow icon="🕐" label="Time" value={`${fmtTime(bStart(booking))} – ${fmtTime(bEnd(booking))}`} />
                {booking.purpose && <InfoRow icon="📋" label="Purpose" value={booking.purpose} />}
                {bCost(booking) != null && <InfoRow icon="💰" label="Cost" value={`$${bCost(booking)} (${booking.paymentStatus || booking.payment_status || 'unpaid'})`} />}
              </div>
              <div>
                <SectionLabel style={{ marginBottom: 12 }}>Customer</SectionLabel>
                <InfoRow icon="👤" label="Name" value={bName(booking)} />
                <InfoRow icon="📧" label="Email" value={bEmail(booking)} />
                <InfoRow icon="📞" label="Phone" value={bPhone(booking)} />
                <InfoRow icon="📆" label="Booked" value={booking.createdAt || booking.created_at ? new Date(booking.createdAt || booking.created_at || '').toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'} />
              </div>
            </div>

            {/* PIN & Protege Sync Status */}
            {bPin(booking) && (
              <div style={{ background: '#0d0d0d', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🔑</span>
                    <span style={{ color: '#a0a0a0', fontSize: 12, fontWeight: 600 }}>ACCESS PIN</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: bSynced(booking) ? '#4ade80' : (booking.protegeSyncError ? '#f87171' : '#facc15') }}>
                      {bSynced(booking) ? '✓ Protege synced' : booking.protegeSyncError ? '✗ Sync failed' : '⏳ Sync pending'}
                    </span>
                    <Btn size="sm" variant="secondary" onClick={() => setPinVisible(p => !p)}>
                      {pinVisible ? 'Hide' : 'Show PIN'}
                    </Btn>
                  </div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 36, fontWeight: 900, letterSpacing: '0.2em', color: '#fff', textAlign: 'center', padding: '8px 0' }}>
                  {pinVisible ? bPin(booking) : '• • • • • •'}
                </div>
              </div>
            )}
            {booking.protegeSyncError && !bSynced(booking) && (
              <Alert type="warning">
                <strong>Protege GX sync failed:</strong> {booking.protegeSyncError}
                {booking.protegeSyncError.includes('lacks write permissions') && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    To fix: in Protege GX admin, grant the SOAP operator account "Add" access to the Cardholders (Users) table, then retry sync.
                  </div>
                )}
              </Alert>
            )}

            {/* Notes */}
            {booking.notes && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>NOTES</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{booking.notes}</div>
              </div>
            )}

            {/* Audit log */}
            {bAudit(booking).length > 0 && (
              <div>
                <SectionLabel style={{ marginBottom: 10 }}>Audit Trail</SectionLabel>
                <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {bAudit(booking).map((e, i) => (
                    <div key={e.id || i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                      <span style={{ color: '#9ca3af', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {new Date(e.createdAt || e.created_at || '').toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                      </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{e.action}</span>
                      {e.details && <span style={{ color: '#6b7280' }}>— {e.details}</span>}
                      {(e.performedBy || e.performed_by) && <span style={{ marginLeft: 'auto', color: '#9ca3af', flexShrink: 0 }}>{e.performedBy || e.performed_by}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Booking not found</div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f8fafc', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#111827', fontWeight: 500, wordBreak: 'break-word' }}>{value}</div>
      </div>
    </div>
  );
}

// ── Recurring Modal ───────────────────────────────────────────────────────────
function RecurringModal({ spaces, onClose, onSave }: { spaces: BookingSpace[]; onClose: () => void; onSave: () => void }) {
  const [form, setForm] = useState({
    spaceId: spaces[0]?.id || '', pattern: 'weekly' as 'daily' | 'weekly' | 'monthly',
    startDate: todayStr(), endDate: '', startTime: '09:00', endTime: '10:00',
    firstName: '', lastName: '', email: '', phone: '', purpose: '',
    daysOfWeek: [] as number[],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const f = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(p => ({ ...p, [k]: e.target.value }));
  const toggleDay = (i: number) => setForm(p => ({ ...p, daysOfWeek: p.daysOfWeek.includes(i) ? p.daysOfWeek.filter(x => x !== i) : [...p.daysOfWeek, i] }));

  const save = async () => {
    if (!form.spaceId || !form.startDate || !form.endDate || !form.email) { setErr('Space, dates, and email are required'); return; }
    setSaving(true); setErr('');
    try { await apiFetch(`${API}/recurring`, { method: 'POST', body: JSON.stringify(form) }); onSave(); }
    catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '32px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ background: '#7c3aed', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>🔁 New Recurring Series</div>
          <button onClick={onClose} style={{ color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {err && <Alert type="error">{err}</Alert>}
          <Field label="Venue Space *">
            <select style={SELECT_STYLE} value={form.spaceId} onChange={f('spaceId')}>
              {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Pattern">
              <select style={SELECT_STYLE} value={form.pattern} onChange={f('pattern') as any}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field label="Start Date *"><input type="date" style={INPUT_STYLE} value={form.startDate} onChange={f('startDate')} /></Field>
            <Field label="End Date *"><input type="date" style={INPUT_STYLE} value={form.endDate} onChange={f('endDate')} /></Field>
          </div>
          {form.pattern === 'weekly' && (
            <Field label="Days of Week">
              <div style={{ display: 'flex', gap: 6 }}>
                {DAY_LABELS.map((d, i) => (
                  <button key={i} onClick={() => toggleDay(i)}
                    style={{ flex: 1, padding: '6px 2px', borderRadius: 6, border: `1.5px solid ${form.daysOfWeek.includes(i) ? '#1d4ed8' : '#d1d5db'}`, background: form.daysOfWeek.includes(i) ? '#1d4ed8' : '#fff', color: form.daysOfWeek.includes(i) ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    {d}
                  </button>
                ))}
              </div>
            </Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Start Time"><input type="time" style={INPUT_STYLE} value={form.startTime} onChange={f('startTime')} /></Field>
            <Field label="End Time"><input type="time" style={INPUT_STYLE} value={form.endTime} onChange={f('endTime')} /></Field>
          </div>
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
            <SectionLabel style={{ marginBottom: 12 }}>Recurring Booker</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="First Name"><input style={INPUT_STYLE} value={form.firstName} onChange={f('firstName')} /></Field>
              <Field label="Last Name"><input style={INPUT_STYLE} value={form.lastName} onChange={f('lastName')} /></Field>
              <Field label="Email *"><input type="email" style={INPUT_STYLE} value={form.email} onChange={f('email')} /></Field>
              <Field label="Phone"><input type="tel" style={INPUT_STYLE} value={form.phone} onChange={f('phone')} /></Field>
            </div>
          </div>
          <Field label="Purpose">
            <input style={INPUT_STYLE} value={form.purpose} onChange={f('purpose')} placeholder="e.g. Weekly committee meeting" />
          </Field>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
          <Btn onClick={onClose} variant="secondary">Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create Series'}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard KPIs ────────────────────────────────────────────────────────────
function Dashboard({ stats, loading }: { stats: DashboardStats | null; loading: boolean }) {
  if (loading) return <Spinner />;
  if (!stats) return null;
  const kpis = [
    { label: "Today's Bookings", value: stats.todayCount,   color: '#60a5fa' },
    { label: 'Active Now',       value: stats.activeCount,  color: '#16a34a' },
    { label: 'Upcoming',         value: stats.upcomingCount, color: '#a78bfa' },
    { label: 'Pending Review',   value: stats.pendingCount, color: '#f59e0b' },
    { label: "Today's Revenue",  value: `$${(+(stats.todayRevenue ?? 0)).toFixed(0)}`, color: '#22d3ee' },
    { label: 'Week Revenue',     value: `$${(+(stats.weekRevenue ?? 0)).toFixed(0)}`,  color: '#34d399' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1, border: '1px solid #222' }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: '#111', border: '1px solid #222', padding: '12px 14px' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
            <div style={{ fontSize: 9, color: '#555', marginTop: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k.label}</div>
          </div>
        ))}
      </div>
      {(stats.spaceUtilisation || []).length > 0 && (
        <div style={{ background: '#111', border: '1px solid #222', padding: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#555', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Space Utilisation — Last 7 Days</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(stats.spaceUtilisation || []).map(s => (
              <div key={s.spaceId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: '#a0a0a0', width: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{s.spaceName}</span>
                <div style={{ flex: 1, background: '#1a1a1a', height: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, s.rate ?? 0)}%`, background: '#3b82f6', transition: 'width 0.4s ease' }} />
                </div>
                <span style={{ fontSize: 10, color: '#555', width: 32, textAlign: 'right', flexShrink: 0 }}>{(s.rate ?? 0).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Protege Integration Settings Panel ───────────────────────────────────────
function ProtegeSettingsPanel() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({ wsdlUrl: '', username: '', password: '', siteId: 6, accessLevelFilter: '' });
  // Split accessLevelFilter into RG part and name-contains part for the UI
  const [rgFilter, setRgFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [status, setStatus] = useState<{ configured: boolean; hasPassword: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    apiFetch(`${API}/protege-settings`)
      .then(d => {
        setStatus({ configured: d.configured, hasPassword: d.hasPassword });
        const raw = d.accessLevelFilter || '';
        const [rg, namePart] = raw.includes('|') ? raw.split('|', 2) : [raw, ''];
        setRgFilter(rg);
        setNameFilter(namePart);
        setCfg(c => ({ ...c, wsdlUrl: d.wsdlUrl || '', username: d.username || '', siteId: d.siteId || 6, accessLevelFilter: raw }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Combine RG + name filter into the accessLevelFilter field before saving
  const buildFilter = () => {
    const rg = rgFilter.trim();
    const nf = nameFilter.trim();
    return rg && nf ? `${rg}|${nf}` : rg;
  };

  const save = async () => {
    if (!cfg.wsdlUrl || !cfg.username) { toast('WSDL URL and username are required', 'error'); return; }
    setSaving(true);
    try {
      await apiFetch(`${API}/protege-settings`, { method: 'PUT', body: JSON.stringify({ ...cfg, accessLevelFilter: buildFilter() }) });
      toast('Protege settings saved', 'success');
      setStatus(s => ({ ...(s || { configured: true, hasPassword: true }), configured: true, hasPassword: true }));
      setCfg(c => ({ ...c, password: '' }));
    } catch (e: any) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await apiFetch(`${API}/protege-settings/test`, { method: 'POST', body: JSON.stringify(cfg) });
      setTestResult(r);
    } catch (e: any) { setTestResult({ ok: false, message: e.message }); }
    finally { setTesting(false); }
  };

  if (loading) return <Spinner />;

  const isConfigured = status?.configured;

  return (
    <div style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Status banner */}
      <div style={{
        borderRadius: 12, padding: '14px 18px',
        background: isConfigured ? '#f0fdf4' : '#fffbeb',
        border: `1px solid ${isConfigured ? '#86efac' : '#fde68a'}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          background: isConfigured ? '#dcfce7' : '#fef3c7',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 17,
        }}>
          {isConfigured ? '✓' : '!'}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: isConfigured ? '#166534' : '#92400e' }}>
            {isConfigured ? 'Protege GX connected' : 'Protege GX not configured'}
          </div>
          <div style={{ fontSize: 12, color: isConfigured ? '#15803d' : '#b45309', marginTop: 1 }}>
            {isConfigured
              ? 'Cardholder provisioning is active. Confirmed bookings automatically create Protege access.'
              : 'Enter the dedicated booking SOAP account credentials below to enable PIN-based door access.'}
          </div>
        </div>
      </div>

      {/* Why separate account — collapsible info */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e0e7ff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#4338ca' }}>i</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#111111', fontSize: 13, marginBottom: 6 }}>Why a dedicated account?</div>
            <div style={{ fontSize: 12.5, color: '#555555', lineHeight: 1.65 }}>
              The Bookings module uses its own Protege GX SOAP operator — completely separate from the Access Control module.
              This lets you deploy Bookings to councils without exposing your full Protege system. The booking account should only have access to the <strong>Bookable</strong> record group.
            </div>
            <div style={{ marginTop: 10, background: '#eef2ff', borderRadius: 7, padding: '9px 12px', fontSize: 12, color: '#3730a3', lineHeight: 1.55 }}>
              <strong>Protege GX setup:</strong> Administration → Operators → New Operator → set username &amp; password → Permissions: grant <em>Add/Edit/Delete</em> on <em>Users</em>, filtered to the <strong>Bookable</strong> record group only.
            </div>
          </div>
        </div>
      </div>

      {/* Credentials form */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        {/* Card header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#1d4ed8' }}>
            ⚙
          </div>
          <div style={{ fontWeight: 700, color: '#111111', fontSize: 14 }}>Connection Credentials</div>
        </div>

        <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="WSDL URL" hint="e.g. http://192.168.1.100/GXWCF2/DataService.svc?wsdl">
            <input style={INPUT_STYLE} value={cfg.wsdlUrl}
              onChange={e => setCfg(c => ({ ...c, wsdlUrl: e.target.value }))}
              placeholder="http://protege-server/GXWCF2/DataService.svc?wsdl" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="SOAP Username" hint="Dedicated booking operator account">
              <input style={INPUT_STYLE} value={cfg.username}
                onChange={e => setCfg(c => ({ ...c, username: e.target.value }))}
                placeholder="bookings-operator" />
            </Field>
            <Field label="Site ID" hint="Default is 6 — check Protege GX Sites">
              <input type="number" style={INPUT_STYLE} value={cfg.siteId}
                onChange={e => setCfg(c => ({ ...c, siteId: Number(e.target.value) }))} />
            </Field>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111111', marginBottom: 2 }}>Access Level Filter</div>
            <Field
              label="Record Group ID or Name"
              hint="The Protege GX Record Group that contains bookable access levels — e.g. '57' or 'Bookable'. Leave blank to show all."
            >
              <input type="text" style={{ ...INPUT_STYLE, maxWidth: 200 }}
                value={rgFilter}
                onChange={e => setRgFilter(e.target.value)}
                placeholder="e.g. 57 or Bookable" />
            </Field>
            <Field
              label="Name contains (optional)"
              hint="Comma-separated keywords — only rooms whose names contain at least one keyword are shown. E.g. 'Room,Hall,Centre,Community,Theatre'. Leave blank to show all rooms in the record group."
            >
              <input type="text" style={INPUT_STYLE}
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                placeholder="e.g. Room,Hall,Centre,Community,Theatre,Studio" />
            </Field>
            {(rgFilter || nameFilter) && (
              <div style={{ fontSize: 11, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', fontFamily: 'monospace' }}>
                Filter: <strong>{buildFilter() || '(none)'}</strong>
              </div>
            )}
          </div>

          <Field label={status?.hasPassword ? 'Password (leave blank to keep existing)' : 'Password *'}>
            <div style={{ position: 'relative' }}>
              <input type={showPass ? 'text' : 'password'} style={{ ...INPUT_STYLE, paddingRight: 44 }}
                value={cfg.password}
                onChange={e => setCfg(c => ({ ...c, password: e.target.value }))}
                placeholder={status?.hasPassword ? '••••••••' : 'Enter SOAP account password'} />
              <button onClick={() => setShowPass(s => !s)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#a0a0a0', padding: 2 }}>
                {showPass ? 'hide' : 'show'}
              </button>
            </div>
          </Field>

          {testResult && (
            <Alert type={testResult.ok ? 'success' : 'error'}>{testResult.message}</Alert>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
            <Btn onClick={test} variant="secondary" disabled={testing || !cfg.wsdlUrl || !cfg.username || !cfg.password}>
              {testing ? 'Testing connection…' : 'Test Connection'}
            </Btn>
            <Btn onClick={save} disabled={saving || !cfg.wsdlUrl || !cfg.username}>
              {saving ? 'Saving…' : 'Save Settings'}
            </Btn>
          </div>
        </div>
      </div>

      {/* Next step — only shown when configured */}
      {isConfigured && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#dcfce7', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#16a34a' }}>→</div>
          <div>
            <div style={{ fontWeight: 700, color: '#166534', fontSize: 13, marginBottom: 4 }}>Next: map your spaces</div>
            <div style={{ fontSize: 12.5, color: '#15803d', lineHeight: 1.6 }}>
              Go to the <strong>Spaces</strong> tab, edit each venue space, and choose the Protege GX access level that controls the door for that room.
              The booking account must have access to that record group.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Availability Gantt ────────────────────────────────────────────────────────
function AvailabilityGantt({ spaceId, spaceName, date }: { spaceId: string; spaceName: string; date: string }) {
  const [slots, setSlots] = useState<{start: string; end: string; available: boolean}[]>([]);
  const [loading, setLoading] = useState(false);
  const START_H = 7; const END_H = 22;
  const totalMins = (END_H - START_H) * 60;

  useEffect(() => {
    if (!spaceId || !date) return;
    setLoading(true);
    fetch('/api/public/bookings/availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ space_id: spaceId, date }),
    }).then(r => r.json()).then(d => setSlots(d.slots || [])).catch(() => setSlots([])).finally(() => setLoading(false));
  }, [spaceId, date]);

  function toMins(t: string) {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0) - START_H * 60;
  }
  function pct(mins: number) { return Math.max(0, Math.min(100, (mins / totalMins) * 100)); }

  const hourMarks: number[] = [];
  for (let h = START_H; h <= END_H; h += 1) hourMarks.push(h);

  // current time line
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes() - START_H * 60;
  const nowPct = pct(nowMins);
  const showNow = nowMins >= 0 && nowMins <= totalMins;

  return (
    <div style={{ background: '#0d0d0d', border: '1px solid #222', padding: '12px 0 8px' }}>
      <div style={{ padding: '0 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#a0a0a0' }}>{spaceName}</span>
        <span style={{ fontSize: 9, color: '#555', fontFamily: 'monospace' }}>{date}</span>
      </div>
      {loading ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: 10, color: '#555' }}>Loading…</div>
      ) : (
        <div style={{ position: 'relative', margin: '0 12px' }}>
          {/* Hour marks */}
          <div style={{ display: 'flex', position: 'relative', height: 14, marginBottom: 2 }}>
            {hourMarks.map(h => (
              <div key={h} style={{ position: 'absolute', left: `${pct((h - START_H) * 60)}%`, fontSize: 8, color: '#555', fontFamily: 'monospace', transform: 'translateX(-50%)' }}>
                {String(h).padStart(2,'0')}
              </div>
            ))}
          </div>
          {/* Track */}
          <div style={{ position: 'relative', height: 28, background: '#1a1a1a', border: '1px solid #222', overflow: 'hidden' }}>
            {/* Hour grid lines */}
            {hourMarks.map(h => (
              <div key={h} style={{ position: 'absolute', left: `${pct((h - START_H) * 60)}%`, top: 0, bottom: 0, width: 1, background: '#1f1f1f' }} />
            ))}
            {/* Slot blocks */}
            {slots.map((sl, i) => {
              const startM = toMins(sl.start);
              const endM   = toMins(sl.end);
              if (endM <= 0 || startM >= totalMins) return null;
              const left = pct(Math.max(0, startM));
              const width = pct(endM - startM);
              const color = sl.available ? '#16a34a' : '#dc2626';
              return (
                <div key={i} style={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${left}%`, width: `${width}%`,
                  background: sl.available ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)',
                  borderLeft: `2px solid ${color}`,
                }} />
              );
            })}
            {/* Current time line */}
            {showNow && (
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${nowPct}%`, width: 1, background: '#dc2626', zIndex: 2 }} />
            )}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, borderLeft: '2px solid #16a34a', background: 'rgba(22,163,74,0.15)' }} />
              <span style={{ fontSize: 8, color: '#555' }}>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, borderLeft: '2px solid #dc2626', background: 'rgba(220,38,38,0.15)' }} />
              <span style={{ fontSize: 8, color: '#555' }}>Booked</span>
            </div>
            {showNow && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 1, height: 8, background: '#dc2626' }} /><span style={{ fontSize: 8, color: '#555' }}>Now</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Room Detail Sliding Panel ─────────────────────────────────────────────────
function RoomDetailPanel({ space: s, onClose, onBook, date }: { space: BookingSpace; onClose: () => void; onBook: () => void; date: string }) {
  const secured = !!s.protegeAccessLevelId;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
        background: '#0d0d0d', borderLeft: '1px solid #222',
        zIndex: 201, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e5e5e5' }}>{s.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: secured ? '#16a34a' : '#f59e0b' }} />
              <span style={{ fontSize: 9, color: secured ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>{secured ? 'Secured' : 'Access Required'}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 2, color: '#555', width: 24, height: 24, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Photo */}
          <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0a0a0a', overflow: 'hidden' }}>
            {s.imageUrl
              ? <img src={s.imageUrl} alt={s.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, opacity: 0.08 }}>&#9974;</div>
            }
          </div>
          {/* DETAILS */}
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>DETAILS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {s.capacity != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Capacity</span><span style={{ color: '#a0a0a0' }}>{s.capacity} people</span></div>}
              {s.category && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Category</span><span style={{ color: '#a0a0a0' }}>{s.category}</span></div>}
              {s.location && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Location</span><span style={{ color: '#a0a0a0' }}>{s.location}</span></div>}
              {s.hourlyRate && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Rate</span><span style={{ color: '#a0a0a0' }}>${s.hourlyRate}/hr</span></div>}
              {s.availableFrom && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Hours</span><span style={{ color: '#a0a0a0' }}>{s.availableFrom}–{s.availableTo}</span></div>}
            </div>
          </div>
          {/* AMENITIES */}
          {(s.amenities || []).length > 0 && (
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>AMENITIES</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {(s.amenities || []).map(a => (
                  <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: '#16a34a', flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 10, color: '#a0a0a0' }}>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* SECURITY */}
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>SECURITY</div>
            <div style={{ background: '#111', border: '1px solid #222', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#555' }}>Access Control</span>
                <span style={{ color: secured ? '#16a34a' : '#f59e0b' }}>{secured ? 'Protege GX Linked' : 'Not Configured'}</span>
              </div>
              {s.autoConfirm && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span style={{ color: '#555' }}>Confirmation</span><span style={{ color: '#60a5fa' }}>Auto-confirm</span></div>}
            </div>
          </div>
          {/* AVAILABILITY Gantt */}
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>AVAILABILITY — {date}</div>
            <AvailabilityGantt spaceId={s.id} spaceName={s.name} date={date} />
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #1a1a1a', flexShrink: 0 }}>
          <button onClick={onBook} style={{
            width: '100%', height: 28, background: '#1e3a5f', border: '1px solid #3b82f6',
            color: '#3b82f6', fontSize: 10, fontWeight: 700, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>BOOK THIS ROOM</button>
        </div>
      </div>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function Bookings() {
  type TabId = 'dashboard' | 'bookings' | 'spaces' | 'recurring' | 'settings' | 'availability';
  const [tab, setTab] = useState<TabId>('spaces');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [spaces, setSpaces] = useState<BookingSpace[]>([]);
  const [recurring, setRecurring] = useState<RecurringTemplate[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;

  const [statusFilter, setStatusFilter] = useState('');
  const [spaceFilter, setSpaceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchDisplay, setSearchDisplay] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [editSpace, setEditSpace] = useState<Partial<BookingSpace> | null | false>(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<BookingSpace | null>(null);
  const [availDate, setAvailDate] = useState<string>(() => new Date().toISOString().slice(0,10));

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await apiFetch(`${API}/dashboard`)); } catch {}
    finally { setStatsLoading(false); }
  }, []);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: String(perPage) });
      if (statusFilter) p.set('status', statusFilter);
      if (spaceFilter) p.set('space_id', spaceFilter);
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      if (search) p.set('q', search);
      const res = await apiFetch(`${API}/?${p}`);
      setBookings(res.bookings || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      console.error('loadBookings error:', e.message);
    }
    finally { setLoading(false); }
  }, [page, statusFilter, spaceFilter, dateFrom, dateTo, search]);

  const loadSpaces = useCallback(async () => {
    try { setSpaces((await apiFetch(`${API}/spaces`)).spaces || []); } catch {}
  }, []);

  const loadRecurring = useCallback(async () => {
    try { setRecurring((await apiFetch(`${API}/recurring`)).templates || []); } catch {}
  }, []);

  useEffect(() => { loadStats(); loadSpaces(); }, [loadStats, loadSpaces]);
  useEffect(() => { if (tab === 'bookings') loadBookings(); else if (tab === 'dashboard') loadStats(); }, [tab, loadBookings, loadStats]);
  useEffect(() => { if (tab === 'recurring') loadRecurring(); }, [tab, loadRecurring]);

  const { toast } = useToast();

  const deleteSpace = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try { await apiFetch(`${API}/spaces/${id}`, { method: 'DELETE' }); loadSpaces(); toast('Space deleted', 'success'); }
    catch (e: any) { toast(e.message, 'error'); }
  };

  const deleteRecurring = async (id: string) => {
    if (!window.confirm('Cancel this recurring series?')) return;
    try { await apiFetch(`${API}/recurring/${id}`, { method: 'DELETE' }); loadRecurring(); toast('Series cancelled', 'success'); }
    catch (e: any) { toast(e.message, 'error'); }
  };

  const TABS: { id: TabId; label: string }[] = [
    { id: 'spaces',       label: 'ROOMS' },
    { id: 'bookings',     label: 'BOOKINGS QUEUE' },
    { id: 'availability', label: 'AVAILABILITY' },
    { id: 'dashboard',    label: 'OVERVIEW' },
    { id: 'recurring',    label: 'RECURRING' },
    { id: 'settings',     label: 'SETTINGS' },
  ];

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  return (
    <Layout title="Bookings" subtitle="Venue booking management and access control">
    <div style={{ padding: '0 0 40px', background: '#0a0a0a', fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>

      {/* Page header */}
      <div style={{ background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 36 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#e5e5e5', textTransform: 'uppercase', letterSpacing: '0.08em' }}>BOOKINGS</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => setShowNewBooking(true)} disabled={spaces.length === 0}
              style={{ height: 26, padding: '0 10px', background: '#16a34a', border: 'none', borderRadius: 0, color: '#fff', fontSize: 10, fontWeight: 700, cursor: spaces.length === 0 ? 'not-allowed' : 'pointer', letterSpacing: '0.04em' }}>
              + NEW BOOKING
            </button>
            <a href="/book" target="_blank" rel="noopener noreferrer"
              style={{ height: 26, padding: '0 10px', background: '#1a1a1a', border: '1px solid #333', color: '#888', fontSize: 10, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', letterSpacing: '0.04em' }}>
              EXPORT
            </a>
          </div>
        </div>

        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id as TabId)}
              style={{ height: 32, padding: '0 14px', border: 'none', borderBottom: `2px solid ${tab === t.id ? '#3b82f6' : 'transparent'}`, background: 'transparent', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: tab === t.id ? '#e5e5e5' : '#444', letterSpacing: '0.06em', position: 'relative', transition: 'color 0.12s' }}>
              {t.label}
              {t.id === 'bookings' && tab !== 'bookings' && pendingCount > 0 && (
                <span style={{ position: 'absolute', top: 3, right: 3, background: '#dc2626', color: '#fff', borderRadius: 2, fontSize: 8, fontWeight: 800, padding: '0 3px', lineHeight: '12px' }}>{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px 20px 0' }}>

        {/* ── OVERVIEW ── */}
        {tab === 'dashboard' && <Dashboard stats={stats} loading={statsLoading} />}

        {/* ── BOOKINGS QUEUE ── */}
        {tab === 'bookings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Filter bar */}
            <div style={{ background: '#111', border: '1px solid #1a1a1a', padding: '8px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input placeholder="Search name, email, ref…" value={searchDisplay}
                onChange={e => { const v = e.target.value; setSearchDisplay(v); if (searchTimeout.current) clearTimeout(searchTimeout.current); searchTimeout.current = setTimeout(() => { setSearch(v); setPage(1); }, 350); }}
                style={{ ...INPUT_STYLE, flex: '1 1 180px', maxWidth: 220 }} />
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...SELECT_STYLE, flex: '0 0 auto', width: 'auto', minWidth: 120 }}>
                <option value="">All Statuses</option>
                {['pending','confirmed','active','completed','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <select value={spaceFilter} onChange={e => { setSpaceFilter(e.target.value); setPage(1); }} style={{ ...SELECT_STYLE, flex: '0 0 auto', width: 'auto', minWidth: 130 }}>
                <option value="">All Rooms</option>
                {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ ...INPUT_STYLE, width: 130 }} />
              <span style={{ color: '#333', fontSize: 10 }}>–</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ ...INPUT_STYLE, width: 130 }} />
              <button onClick={loadBookings} style={{ padding: '5px 8px', background: '#1a1a1a', border: '1px solid #333', borderRadius: 2, cursor: 'pointer', fontSize: 12, color: '#555' }} title="Refresh">⟳</button>
              {(statusFilter || spaceFilter || dateFrom || dateTo) && (
                <Btn size="sm" variant="ghost" onClick={() => { setStatusFilter(''); setSpaceFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}>Clear</Btn>
              )}
            </div>

            {/* Dense table */}
            <div style={{ background: '#111', border: '1px solid #1a1a1a', overflow: 'hidden' }}>
              {loading ? <Spinner /> : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#0d0d0d', borderBottom: '1px solid #222' }}>
                        {['ID','ROOM','REQUESTED BY','DATE','START','END','PURPOSE','STATUS','ACTIONS'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 9, fontWeight: 700, color: '#444', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 ? (
                        <tr><td colSpan={9} style={{ textAlign: 'center', padding: '32px 0', color: '#333' }}>
                          <div style={{ fontSize: 11, color: '#444', fontWeight: 600 }}>No bookings found</div>
                          <div style={{ fontSize: 10, marginTop: 4, color: '#333' }}>Adjust filters or date range</div>
                        </td></tr>
                      ) : bookings.map(b => (
                        <tr key={b.id} onClick={() => setDetailId(b.id)} style={{ borderBottom: '1px solid #1a1a1a', cursor: 'pointer' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#141414'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: 10, color: '#60a5fa', fontWeight: 700, whiteSpace: 'nowrap' }}>{b.reference}</td>
                          <td style={{ padding: '5px 10px', color: '#a0a0a0', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{bSpace(b)}</td>
                          <td style={{ padding: '5px 10px' }}>
                            <div style={{ fontSize: 11, color: '#e5e5e5', fontWeight: 500 }}>{bName(b)}</div>
                            <div style={{ fontSize: 9, color: '#555' }}>{bEmail(b)}</div>
                          </td>
                          <td style={{ padding: '5px 10px', color: '#888', whiteSpace: 'nowrap', fontSize: 10 }}>{fmtDate(bDate(b))}</td>
                          <td style={{ padding: '5px 10px', color: '#888', whiteSpace: 'nowrap', fontSize: 10, fontFamily: 'monospace' }}>{bStart(b)}</td>
                          <td style={{ padding: '5px 10px', color: '#888', whiteSpace: 'nowrap', fontSize: 10, fontFamily: 'monospace' }}>{bEnd(b)}</td>
                          <td style={{ padding: '5px 10px', color: '#555', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{b.purpose || '—'}</td>
                          <td style={{ padding: '5px 10px' }}><StatusPill status={b.status} /></td>
                          <td style={{ padding: '5px 10px' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={e => { e.stopPropagation(); setDetailId(b.id); }}
                                style={{ height: 20, padding: '0 7px', background: '#1e3a5f', border: '1px solid #1e3a5f', borderRadius: 2, cursor: 'pointer', color: '#60a5fa', fontSize: 9, fontWeight: 700 }}>
                                VIEW
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {total > perPage && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, color: '#555' }}>
                <span>{total} bookings total</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Btn size="sm" variant="secondary" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>← Prev</Btn>
                  <span style={{ fontWeight: 600, color: '#888' }}>Page {page} of {Math.ceil(total / perPage)}</span>
                  <Btn size="sm" variant="secondary" onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(total / perPage)}>Next →</Btn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ROOMS ── */}
        {tab === 'spaces' && (
          <div>
            {spaces.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', background: '#111', border: '1px solid #1a1a1a' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#444', marginBottom: 8 }}>No rooms configured yet</div>
                <div style={{ fontSize: 10, color: '#333', marginBottom: 16 }}>Add your first bookable venue space to get started</div>
                <Btn onClick={() => setEditSpace({})}>+ Add First Room</Btn>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, border: '1px solid #222', borderRight: 'none', borderBottom: 'none' }}>
                {spaces.map(s => (
                  <div key={s.id} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222' }}>
                    <SpaceCard space={s} onEdit={() => setEditSpace(s)} onDelete={() => deleteSpace(s.id, s.name || '')} onSelect={() => setSelectedSpace(s)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AVAILABILITY ── */}
        {tab === 'availability' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={selectedSpace?.id || ''} onChange={e => { const sp = spaces.find(s => s.id === e.target.value); setSelectedSpace(sp || null); }}
                style={{ ...SELECT_STYLE, width: 'auto', minWidth: 200 }}>
                <option value="">Select a room…</option>
                {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input type="date" value={availDate} onChange={e => setAvailDate(e.target.value)} style={{ ...INPUT_STYLE, width: 150 }} />
            </div>
            {selectedSpace ? (
              <AvailabilityGantt spaceId={selectedSpace.id} spaceName={selectedSpace.name} date={availDate} />
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', background: '#111', border: '1px solid #1a1a1a', color: '#333', fontSize: 11 }}>
                Select a room to view availability
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && <ProtegeSettingsPanel />}

        {/* ── RECURRING ── */}
        {tab === 'recurring' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {recurring.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', background: '#111', border: '1px solid #1a1a1a' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#444', marginBottom: 6 }}>No recurring series</div>
                <div style={{ fontSize: 10, color: '#333', marginBottom: 14 }}>Create recurring booking templates for regular hirers</div>
                <Btn onClick={() => setShowRecurring(true)} disabled={spaces.length === 0}>+ New Series</Btn>
              </div>
            ) : recurring.map(r => (
              <div key={r.id} style={{ background: '#111', border: '1px solid #1a1a1a', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#e5e5e5', fontSize: 11 }}>{r.spaceName || r.space_name}</span>
                    <span style={{ background: '#1e1b4b', color: '#a78bfa', border: '1px solid #312e81', padding: '1px 6px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.pattern}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <div style={{ fontSize: 10, color: '#555' }}>
                    {r.firstName} {r.lastName} · {r.startDate || r.start_date} → {r.endDate || r.end_date} · {fmtTime(r.startTime || r.start_time)} – {fmtTime(r.endTime || r.end_time)}
                  </div>
                  {r.email && <div style={{ fontSize: 9, color: '#444' }}>{r.email}</div>}
                </div>
                <button onClick={() => deleteRecurring(r.id)} title="Cancel series"
                  style={{ padding: '4px 8px', background: '#450a0a', border: '1px solid #7f1d1d', color: '#dc2626', borderRadius: 2, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Modals */}
      {detailId && <BookingDetailModal bookingId={detailId} onClose={() => setDetailId(null)} onRefresh={loadBookings} />}
      {editSpace !== false && (
        <SpaceModal space={editSpace as Partial<BookingSpace>} onClose={() => setEditSpace(false)} onSave={() => { setEditSpace(false); loadSpaces(); }} />
      )}
      {showRecurring && <RecurringModal spaces={spaces} onClose={() => setShowRecurring(false)} onSave={() => { setShowRecurring(false); loadRecurring(); }} />}
      {showNewBooking && <NewBookingModal spaces={spaces} onClose={() => setShowNewBooking(false)} onSave={() => { setTab('bookings' as any); loadBookings(); loadStats(); }} />}
      {selectedSpace && (
        <RoomDetailPanel
          space={selectedSpace}
          date={availDate}
          onClose={() => setSelectedSpace(null)}
          onBook={() => { setSelectedSpace(null); setShowNewBooking(true); }}
        />
      )}
    </div>
    </Layout>
  );
}

// ── Space Card ────────────────────────────────────────────────────────────────
function SpaceCard({ space: s, onEdit, onDelete, onSelect }: { space: BookingSpace; onEdit: () => void; onDelete: () => void; onSelect: () => void }) {
  const [hovBar, setHovBar] = useState(false);
  const secured = !!s.protegeAccessLevelId;
  return (
    <div style={{ background: '#111', border: '1px solid #222', borderRadius: 0, overflow: 'hidden' }}>
      {/* Photo area 16:9 */}
      <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0a0a0a', overflow: 'hidden', cursor: 'pointer' }} onClick={onSelect}>
        {s.imageUrl
          ? <img src={s.imageUrl} alt={s.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, opacity: 0.1, color: '#888' }}>&#9974;</div>
        }
        <div style={{ position: 'absolute', top: 5, right: 5, display: 'flex', gap: 3 }}>
          <button onClick={e => { e.stopPropagation(); onEdit(); }} style={{ width: 20, height: 20, background: 'rgba(0,0,0,0.75)', border: '1px solid #333', borderRadius: 2, color: '#888', cursor: 'pointer', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✏</button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} style={{ width: 20, height: 20, background: 'rgba(0,0,0,0.75)', border: '1px solid #333', borderRadius: 2, color: '#dc2626', cursor: 'pointer', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: 8, cursor: 'pointer' }} onClick={onSelect}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#e5e5e5', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
        {s.capacity != null && <div style={{ fontSize: 10, color: '#888', marginBottom: 3 }}>Capacity: {s.capacity}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: s.amenities?.length ? 3 : 0 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: secured ? '#16a34a' : '#f59e0b', flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: secured ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>{secured ? 'Secured' : 'Access Required'}</span>
        </div>
        {(s.amenities || []).length > 0 && (
          <div style={{ fontSize: 9, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(s.amenities || []).join(' · ')}</div>
        )}
      </div>
      {/* Bottom bar */}
      <div
        onClick={onSelect}
        onMouseEnter={() => setHovBar(true)} onMouseLeave={() => setHovBar(false)}
        style={{ height: 28, background: hovBar ? '#1e3a5f' : '#0a0a0a', borderTop: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.15s' }}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#3b82f6', letterSpacing: '0.06em' }}>VIEW AVAILABILITY</span>
      </div>
    </div>
  );
}
